/**
 * LiveMonitor — FEAT-MONITOR-001 (rendered as the "Message Monitor" tab)
 * Real-time MESSAGE-flow monitor for the Admin portal — incoming vs outgoing only. Aggregate
 * (all accounts) or a single account (selector). Shows incoming/outgoing message TPS, server-
 * capacity headroom (from agent CPU/RAM), a received-vs-sent throughput chart, and per-box
 * TPS sources (from agent server-log tailing). Server/datastore health lives in the
 * Infrastructure tab, not here. Polls via useMonitorPolling; dims on a failed poll, never blanks.
 */
import React, { useMemo, useState } from "react";
import {
  Box, Flex, HStack, Text, Heading, Badge, Button, Select, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, SimpleGrid, Wrap, WrapItem,
} from "@chakra-ui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import useMonitorPolling from "./useMonitorPolling";
import PipelineFlow from "./PipelineFlow";
import {
  TONE, classifyUtil,
  fmtInt, fmtPct, fmtTps, fmtRelative, WINDOW_OPTIONS, BUCKET_HINT,
} from "./helpers";

const INTERVALS = [{ label: "2s", ms: 2000 }, { label: "5s", ms: 5000 }, { label: "10s", ms: 10000 }, { label: "30s", ms: 30000 }, { label: "Off", ms: 0 }];
const BUCKETS = ["1m", "5m", "15m", "1h"];

// Which resource is limiting the incoming-TPS ceiling (from the resource-aware headroom calc).
const BIND_LABEL = { cpu: "CPU-bound", ram: "RAM-bound", tps_cap: "TPS-cap", insufficient_data: "est. paused", no_agents: "no agent" };
const BIND_TONE = { cpu: "orange", ram: "red", tps_cap: "blue", insufficient_data: "gray", no_agents: "gray" };

export default function LiveMonitor() {
  const [intervalMs, setIntervalMs] = useState(2000); // near-real-time (service caches 2s)
  const [window, setWindow] = useState("6h");
  const [bucket, setBucket] = useState(BUCKET_HINT["6h"]);
  const [now, setNow] = useState(Date.now());

  // The standalone admin-monitor service is all-accounts (no per-customer scoping — that needed
  // MySQL). Per-account utilization, per-channel and delivery-quality tiles were removed with the
  // no-DB backend; TPS here is derived from the metrics agents' server-log tailing.
  const { overview, throughput, pipeline, error, stale, lastOkAt, busy, refresh } =
    useMonitorPolling({ intervalMs, window, bucket });

  React.useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const staleSec = lastOkAt ? Math.max(0, Math.round((now - new Date(lastOkAt).getTime()) / 1000)) : null;
  // Message-flow health only (incoming/outgoing) — service/infra health lives in the Infrastructure tab.
  const capH = overview?.capacity;
  const health = !overview ? "muted"
    : (capH?.utilization_pct > 90 || (staleSec != null && staleSec > 60)) ? "crit"
    : (capH?.utilization_pct > 70 || (staleSec != null && staleSec > 15)) ? "warn"
    : "ok";

  const changeWindow = (w) => { setWindow(w); setBucket(BUCKET_HINT[w] || "5m"); };

  const cap = overview?.capacity;

  return (
    <Box opacity={stale ? 0.7 : 1} transition="opacity .2s">
      {/* Header */}
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3} mb={5}>
        <HStack spacing={3} align="center">
          <Heading fontSize="22px" color="#111827">Message Monitor</Heading>
          <HealthPill health={health} />
          {stale && <Badge colorScheme="orange" variant="subtle">Stale</Badge>}
          <Text fontSize="xs" color="gray.500">
            {staleSec != null ? `updated ${fmtRelative(lastOkAt)}` : "polling…"}
            {busy && <Spinner size="xs" ml={2} />}
          </Text>
        </HStack>
        <Wrap spacing={2} align="center">
          <WrapItem>
            <Select size="sm" w="90px" value={intervalMs} onChange={(e) => setIntervalMs(Number(e.target.value))}>
              {INTERVALS.map((i) => <option key={i.label} value={i.ms}>{i.label}</option>)}
            </Select>
          </WrapItem>
          <WrapItem><Button size="sm" onClick={refresh} isLoading={busy}>Refresh</Button></WrapItem>
        </Wrap>
      </Flex>

      {error && !overview && (
        <Box p={3} mb={4} bg={TONE.crit.bg} color={TONE.crit.fg} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      {/* Pipeline flow — inflow → buffer → outflow → delivery, with bottleneck verdict */}
      {pipeline && <PipelineFlow pipeline={pipeline} />}

      {/* KPI row */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={5}>
        <Kpi label="Incoming (peak)" value={fmtTps(overview?.incoming?.peak_tps)} sub={`${fmtTps(overview?.incoming?.tps)} now`} tone="ok" />
        <Kpi label="Outgoing (peak)" value={fmtTps(overview?.outgoing?.peak_tps)} sub={`${fmtTps(overview?.outgoing?.tps)} now`} tone="ok" />
        {/* When there's no traffic to estimate against, don't present the raw hard cap as a live
            number — show it's awaiting traffic (the estimate needs a throughput anchor). */}
        {cap?.binding_constraint === "insufficient_data" || cap?.binding_constraint === "no_agents" ? (
          <>
            <Kpi label="Live headroom" value="—" sub={cap?.binding_constraint === "no_agents" ? "no ingest agent" : "awaiting traffic"} tone="muted" />
            <Kpi label="Capacity used" value="—" sub={`hard cap ${fmtInt(cap?.server_capacity_tps)} tps`} tone="muted" />
          </>
        ) : (
          <>
            <Kpi label="Live headroom" value={fmtTps(cap?.live_headroom_tps)} sub={BIND_LABEL[cap?.binding_constraint] || `of ${fmtInt(cap?.effective_capacity_tps)} tps`} tone={classifyUtil(cap?.utilization_pct)} />
            <Kpi label="Capacity used" value={fmtPct(cap?.utilization_pct)} sub={`${fmtInt(cap?.current_incoming_peak_tps)} / ${fmtInt(cap?.effective_capacity_tps)} tps`} tone={classifyUtil(cap?.utilization_pct)} />
          </>
        )}
        <Kpi label="Agents" value={`${fmtInt(overview?.agents?.reachable)}/${fmtInt(overview?.agents?.configured)}`} sub="reachable / configured" tone={overview?.agents && overview.agents.reachable < overview.agents.configured ? "warn" : "muted"} />
      </SimpleGrid>

      {/* Capacity meter */}
      {cap && <CapacityMeter cap={cap} />}

      {/* Throughput chart */}
      <Box className="chart_box" mb={4} p={4} bg="white" borderRadius="lg" borderWidth="1px">
        <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
          <Box>
            <Text fontWeight="700" fontSize="sm" color="#111827">Throughput</Text>
            <Text fontSize="xs" color="gray.500">messages received vs sent per {bucket} bucket · all accounts</Text>
          </Box>
          <HStack spacing={2}>
            <HStack spacing={1}>
              {WINDOW_OPTIONS.map((w) => (
                <Button key={w} size="xs" variant={window === w ? "solid" : "outline"} colorScheme="blue" onClick={() => changeWindow(w)}>{w}</Button>
              ))}
            </HStack>
            <Select size="xs" w="70px" value={bucket} onChange={(e) => setBucket(e.target.value)}>
              {BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>
          </HStack>
        </Flex>
        <ThroughputChart series={throughput?.series || []} />
      </Box>

      {/* Per-stream TPS from the agents' server-log tailing (in / out per box). */}
      <Box mb={4}><BySource sources={overview?.sources || []} /></Box>
    </Box>
  );
}

function HealthPill({ health }) {
  const t = TONE[health] || TONE.muted;
  const label = { ok: "Healthy", warn: "Degraded", crit: "Critical", muted: "—" }[health];
  return (
    <HStack spacing={1.5} px={2} py={0.5} borderRadius="full" bg={t.bg}>
      <Box w="8px" h="8px" borderRadius="full" bg={t.dot} />
      <Text fontSize="xs" fontWeight="700" color={t.fg}>{label}</Text>
    </HStack>
  );
}

function Kpi({ label, value, sub, tone }) {
  const t = TONE[tone] || TONE.muted;
  return (
    <Box p={3} bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100">
      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.03em">{label}</Text>
      <Heading fontSize="20px" color={t.fg} mt={1}>{value}</Heading>
      {sub && <Text fontSize="11px" color="gray.400" mt={0.5}>{sub}</Text>}
    </Box>
  );
}

function CapacityMeter({ cap }) {
  // Bar scaled to the resource-aware effective ceiling (min of hard cap / CPU / RAM).
  const ceil = cap.effective_capacity_tps || cap.server_capacity_tps || 1;
  const usedPct = Math.min(100, (cap.current_incoming_peak_tps / ceil) * 100);
  const r = cap.resource || {};
  const constrained = cap.binding_constraint === "cpu" || cap.binding_constraint === "ram";
  const estPaused = cap.binding_constraint === "insufficient_data" || cap.binding_constraint === "no_agents";
  return (
    <Box p={4} mb={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Flex justify="space-between" align="center" mb={2} fontSize="xs" color="gray.600" wrap="wrap" gap={1}>
        <HStack spacing={2}>
          <Text fontWeight="700" color="#111827">Server capacity — incoming TPS</Text>
          <Badge colorScheme={BIND_TONE[cap.binding_constraint] || "gray"} variant="subtle" fontSize="10px">{BIND_LABEL[cap.binding_constraint] || cap.binding_constraint}</Badge>
        </HStack>
        <Text>{fmtInt(cap.current_incoming_peak_tps)} live · {estPaused ? `hard cap ${fmtInt(cap.server_capacity_tps)}` : `effective ceiling ${fmtInt(cap.effective_capacity_tps)} · hard cap ${fmtInt(cap.server_capacity_tps)}`}</Text>
      </Flex>
      <Box position="relative" h="18px" bg="gray.100" borderRadius="full" overflow="hidden">
        <Box position="absolute" left={0} top={0} h="100%" w={`${estPaused ? 0 : usedPct}%`} bg={TONE[classifyUtil(cap.utilization_pct)].dot} />
      </Box>
      <Flex justify="space-between" mt={2} fontSize="xs" wrap="wrap" gap={1}>
        <Text color={estPaused ? "gray.400" : TONE[classifyUtil(cap.utilization_pct)].fg} fontWeight="600">
          {estPaused ? "Awaiting traffic to estimate live headroom" : `${fmtPct(cap.utilization_pct)} used · ${fmtInt(cap.live_headroom_tps)} tps headroom`}
        </Text>
      </Flex>
      {/* Resource basis for the estimate */}
      {(r.cpu_now_pct != null || r.ram_now_pct != null) && (
        <Flex mt={2} pt={2} borderTopWidth="1px" fontSize="11px" color="gray.500" wrap="wrap" gap={4}>
          <Text>ingest CPU <Text as="span" color={r.cpu_now_pct >= r.cpu_target_pct ? TONE.crit.fg : "gray.700"} fontWeight="600">{fmtPct(r.cpu_now_pct)}</Text> / {r.cpu_target_pct}% → ~{fmtInt(r.cpu_ceiling_tps)} tps</Text>
          <Text>ingest RAM <Text as="span" color={r.ram_now_pct >= r.ram_target_pct ? TONE.crit.fg : "gray.700"} fontWeight="600">{fmtPct(r.ram_now_pct)}</Text> / {r.ram_target_pct}% → ~{fmtInt(r.ram_ceiling_tps)} tps</Text>
          {(r.ingest_boxes || []).length > 0 && <Text color="gray.400">from {r.ingest_boxes.join(", ")}</Text>}
        </Flex>
      )}
      {!constrained && (cap.binding_constraint === "no_agents" || cap.binding_constraint === "insufficient_data") && (
        <Text mt={1} fontSize="10px" color="gray.400">
          {cap.binding_constraint === "no_agents" ? "No ingest-tier agent reachable — showing hard cap. Deploy the metrics agent + tag it tier:\"ingest\"." : "Traffic too low to estimate a resource ceiling — showing hard cap."}
        </Text>
      )}
    </Box>
  );
}

function ThroughputChart({ series }) {
  const options = useMemo(() => {
    const cats = series.map((s) => new Date(s.ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    return {
      chart: { type: "area", height: 260 },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories: cats, tickInterval: Math.ceil(cats.length / 12), labels: { style: { fontSize: "10px" } } },
      yAxis: { title: { text: "messages / bucket" }, allowDecimals: false },
      legend: { align: "right", verticalAlign: "top" },
      plotOptions: { area: { fillOpacity: 0.15, marker: { enabled: false }, lineWidth: 2 } },
      tooltip: { shared: true },
      series: [
        { name: "Incoming", data: series.map((s) => s.incoming), color: "#6366F1" },
        { name: "Outgoing", data: series.map((s) => s.outgoing), color: "#10B981" },
      ],
    };
  }, [series]);

  if (!series.length || series.every((s) => s.incoming === 0 && s.outgoing === 0)) {
    return <Flex h="200px" align="center" justify="center" color="gray.400" fontSize="sm">No traffic in this window.</Flex>;
  }
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function BySource({ sources }) {
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Text fontWeight="700" fontSize="sm" color="#111827" mb={2}>By source (per-box TPS from server logs)</Text>
      {!sources.length ? <Text fontSize="xs" color="gray.400">No TPS sources reporting — set TPS_LOG_SOURCES on the agents.</Text> : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead><Tr><Th>Box</Th><Th>Stream</Th><Th>Direction</Th><Th isNumeric>TPS</Th><Th isNumeric>Peak</Th></Tr></Thead>
            <Tbody>
              {sources.map((s, i) => (
                <Tr key={`${s.box}-${s.name}-${i}`}>
                  <Td>{s.box}</Td>
                  <Td>{s.name}</Td>
                  <Td><Badge colorScheme={s.direction === "out" ? "green" : "blue"} variant="subtle">{s.direction}</Badge></Td>
                  <Td isNumeric>{fmtTps(s.tps)}</Td>
                  <Td isNumeric>{fmtInt(s.peak)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
